import * as Sentry from "@sentry/browser";
import { SylaApiError } from "@syla/shared/errors/SylaApiError";
import { DataSourceType } from "@syla/shared/types/models/DataSourceBase";
import { AddWalletType } from "@syla/shared/types/requests/AddWalletRequest";
import { DataSourceWithNetworks } from "@syla/shared/types/responses/DataSourceWithNetworks";
import axios from "axios";
import React, { useEffect, useState, useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { DataSourceInputType } from "../../../../helper/dataSource/DataSourceInputType";
import { syncWallet } from "../../../../store/actions/syncWallet";
import {
  deleteWallet,
  addWallet,
  importWalletCsv,
  importDataSources,
} from "../../../../store/actions/wallet";
import { useCurrentAccountStore } from "../../../../store/currentAccountStore";
import { SyncApiFormFields } from "../components/SyncApiFields";
import { DataSourceTypeFilter } from "../dataSourceSelector/SelectDataSource";
import { getSupportedImportMethods } from "../helpers/getSupportedImportMethods";
import { isCompleted } from "../helpers/isCompleted";
import { CompletingInputPanel } from "../panels/CompletingInputPanel";
import {
  ImportMethodSelectionPanel,
  ImportMethod,
} from "../panels/ImportMethodSelectionPanel";
import { ImportProgressPanel } from "../panels/ImportProgressPanel";
import { SelectDataSourcePanel } from "../panels/SelectDataSourcePanel";
import { ImportStage } from "./ImportStage";

export type AddDataSourceForm = {
  customName?: string;
  objectAddresses: { address: string }[];
  fromDate: Date | undefined;
  storedFiles: File[];
  network?: string;
} & SyncApiFormFields;

export function AddDataSourceWorkflow({
  onClose,
  onComplete,
  setAllowQuickClose,
  enableSkipForNow,
}: {
  onClose?: () => void;
  onComplete: () => void;
  setAllowQuickClose?: (value: boolean) => void;
  enableSkipForNow?: boolean; // for add-first-data-source
}): React.ReactElement {
  const workflow = useAddDataSourceWorkflow();

  // determine whether the user can close the drawer by clicking outside the drawer.
  useEffect(() => {
    if (!setAllowQuickClose) {
      return;
    }

    if (
      workflow.state.stage === "selectDataSource" ||
      isCompleted(workflow.state.stage)
    ) {
      setAllowQuickClose(true);
    } else {
      setAllowQuickClose(false);
    }
  }, [setAllowQuickClose, workflow.state.stage]);

  const form = useForm<AddDataSourceForm>({
    defaultValues: {
      storedFiles: [],
    },
  });

  // search and filter state is held here, so the user can select a data source
  // and still come back without losing their search and filter selections.
  const [searchValue, setSearchValue] = useState("");
  const [filter, setFilter] = useState<DataSourceTypeFilter>("all");

  const handleImport = async (
    values: AddDataSourceForm,
    walletId: string | undefined,
  ) => {
    await workflow.actions.beginImport(values, walletId);
  };

  if (workflow.state.stage === "selectDataSource") {
    return (
      <SelectDataSourcePanel
        enableSkipForNow={enableSkipForNow}
        searchValue={searchValue}
        filter={filter}
        onSearchTermChange={setSearchValue}
        onFilterChange={setFilter}
        onClose={onClose}
        onSelect={(dataSource) => {
          if (dataSource.type === DataSourceType.Custom) {
            form.setValue("customName", searchValue);
          } else {
            form.setValue("customName", dataSource.name);
          }

          workflow.actions.toSelectMethodStage(dataSource);
        }}
      />
    );
  }

  if (workflow.state.stage === "selectImportMethod") {
    return (
      <ImportMethodSelectionPanel
        enableSkipForNow={enableSkipForNow}
        dataSource={workflow.state.dataSource}
        importMethods={getSupportedImportMethods(workflow.state.dataSource)}
        customName={form.watch("customName") ?? ""}
        onSelect={(method) => {
          workflow.actions.toCompletingInputStage(method);
        }}
        onBack={() => {
          workflow.actions.toSelectDataSourceStage();
          form.reset();
        }}
      />
    );
  }

  if (workflow.state.stage === "completingInput") {
    const dataSource = workflow.state.dataSource;
    return (
      <FormProvider {...form}>
        <CompletingInputPanel
          dataSource={dataSource}
          importMethod={workflow.state.importMethod}
          error={workflow.state.importError}
          setError={(error) => workflow.actions.setImportError(error)}
          onImportStart={handleImport}
          onBack={() => {
            form.resetField("storedFiles");
            workflow.actions.toSelectMethodStage(dataSource);
          }}
        />
      </FormProvider>
    );
  }

  return (
    <ImportProgressPanel
      importStage={workflow.state.stage}
      importMethod={workflow.state.importMethod}
      customName={form.watch("customName") ?? ""}
      onComplete={onComplete}
      dataSource={workflow.state.dataSource}
    />
  );
}

type SelectingDataSourceState = {
  stage: "selectDataSource";
  dataSource: undefined;
  importMethod: ImportMethod;
  importError: undefined;
};

type SelectingImportMethodState = {
  stage: "selectImportMethod";
  dataSource: DataSourceWithNetworks;
  importMethod: undefined;
  importError: undefined;
};

type SelectedDataSourceState = {
  stage:
    | "completingInput"
    | "initialising"
    | "importing"
    | "backgroundImporting"
    | "complete";
  dataSource: DataSourceWithNetworks;
  importMethod: ImportMethod;
  importError: Error | undefined;
};

type ImportState =
  | SelectingDataSourceState
  | SelectingImportMethodState
  | SelectedDataSourceState;

export function useAddDataSourceWorkflow() {
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const [stage, setStage] = useState<ImportStage>("selectDataSource");

  const [dataSource, setDataSource] = useState<
    DataSourceWithNetworks | undefined
  >();

  const [importMethod, setImportMethod] = useState<ImportMethod | undefined>();

  const [importError, setImportError] = useState<
    Error | SylaApiError | unknown | undefined
  >();

  const toSelectDataSourceStage = useCallback(() => {
    setStage("selectDataSource");
    setImportMethod(undefined);
    setDataSource(undefined);
    setImportError(undefined);
  }, []);

  const toSelectMethodStage = useCallback(
    (nextDataSource: DataSourceWithNetworks) => {
      setStage("selectImportMethod");
      setImportMethod(undefined);
      setDataSource(nextDataSource);
      setImportError(undefined);
    },
    [],
  );

  const toCompletingInputStage = useCallback((method: ImportMethod) => {
    setStage("completingInput");
    setImportMethod(method);
    setImportError(undefined);
  }, []);

  const beginImport = useCallback(
    // walletId is only needed if OAuth flow was used (which created a new wallet)
    async (props: AddDataSourceForm, walletId?: string) => {
      const {
        apiKey,
        secretKey,
        passphrase,
        fromDate,
        storedFiles,
        customName,
        network,
      } = props;

      if (!dataSource) {
        throw new Error("dataSource expected");
      }

      setImportError(undefined);

      let tempWalletId: string | undefined = walletId;

      try {
        // Empty wallet
        if (importMethod === "Empty") {
          setStage("importing");

          await addWallet(accountId, {
            dataSourceId: dataSource!._id,
            customName,
            type: AddWalletType.Empty,
          });

          setStage("complete");
        }

        // OAuth Sync
        else if (
          importMethod === DataSourceInputType.Api &&
          dataSource?.syncAuthType === "oauth"
        ) {
          // the wallet is created earlier when the user did Auth, so the
          // walletId should be available
          if (!tempWalletId) {
            throw new Error("walletId expected for OAuth sync");
          }

          setStage("backgroundImporting");

          syncWallet(
            accountId,
            { _id: tempWalletId, customName, dataSource },
            fromDate,
          )
            .then(() => {
              setStage("complete");
            })
            .catch((error) => {
              setImportError(error);
            });
          return;
        }

        // API Key or Wallet Address Sync
        else if (importMethod === DataSourceInputType.Api) {
          if (!apiKey) return;
          if (dataSource?.syncImportParams?.secret && !secretKey) return;
          if (dataSource?.syncImportParams?.passphrase && !passphrase) return;
          if (dataSource.type === DataSourceType.Wallet && !network) return;

          setStage("initialising");
          tempWalletId = (
            await addWallet(accountId, {
              dataSourceId: dataSource._id,
              apiKey,
              secretKey,
              passphrase,
              fromDate,
              customName,
              type: AddWalletType.Sync,
              network,
            })
          )._id;

          setStage("backgroundImporting");

          syncWallet(
            accountId,
            { _id: tempWalletId, customName, dataSource },
            fromDate,
          )
            .then(() => {
              setStage("complete");
            })
            .catch((error) => {
              setImportError(error);
            });

          return;
        }

        // CSV import
        else {
          if (!storedFiles || storedFiles.length === 0) {
            throw new Error("Expected at least one file to import");
          }

          setStage("importing");

          if (dataSource.type === DataSourceType.Import) {
            if (!dataSource._id) {
              throw new Error("DataSource Id expected");
            }

            await importDataSources({
              accountId,
              dataSourceId: dataSource._id,
              files: storedFiles,
            });
          } else {
            tempWalletId = (
              await addWallet(accountId, {
                dataSourceId: dataSource._id,
                customName,
                type: AddWalletType.CSV,
              })
            )._id;

            await importWalletCsv(accountId, tempWalletId, storedFiles);
          }

          setStage("complete");
        }
      } catch (error) {
        if (!axios.isAxiosError(error)) {
          Sentry.captureException(error);
        }

        if (error instanceof Error) {
          setImportError(error);
        }

        if (tempWalletId) {
          await deleteWallet(accountId, tempWalletId);
        }

        setStage("completingInput");
      }
    },
    [accountId, dataSource, importMethod],
  );

  return {
    state: {
      stage,
      dataSource,
      importMethod,
      importError,
    } as ImportState,

    actions: {
      setImportError,
      toSelectDataSourceStage,
      toSelectMethodStage,
      toCompletingInputStage,
      beginImport,
    },
  };
}
